import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Table,
  Spinner,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  CardBody,
} from 'reactstrap';
import {
  Button,
  Icon,
} from '../../components/Component';
import Swal from 'sweetalert2';
import classnames from 'classnames';
import { BACKEND } from '../../consts/backend';

const TemporalesMroAvisos = ({ isOpen, toggle, onUploadSuccess }) => {
  // Estados principales
  const user = JSON.parse(localStorage.getItem('user'));
  
  // Estados de la UI
  const [activeTab, setActiveTab] = useState('email');
  const [consoleModal, setConsoleModal] = useState(false);
  const [consoleActiveTab, setConsoleActiveTab] = useState('log');
  const [consoleLog, setConsoleLog] = useState('');
  const [emailAutomationStatus, setEmailAutomationStatus] = useState(null);
  
  // Estados de email config
  const [emailSettings, setEmailSettings] = useState({
    subject: '',
    body: '',
    footer: ''
  });
  
  // Estados de emails guardados
  const [savedEmails, setSavedEmails] = useState([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  
  // Estados de automatización
  const [automationConfig, setAutomationConfig] = useState({
    is_active: false,
    hora: 7,
    dias: '*'
  });
  
  // Estados de loading
  const [loading, setLoading] = useState({
    settings: true,
    savingEmailSettings: false,
    loadingEmails: true,
    savingEmails: false,
    deletingEmails: {},
    sendingEmail: false,
    loadingAutomation: true,
    savingAutomation: false,
    log: false,
    status: false
  });

  // Cargar todo al abrir el modal
  useEffect(() => {
    if (isOpen) {
      loadAllConfiguration();
      loadSavedEmails();
    }
  }, [isOpen]);

  // ============= FUNCIONES DE EMAIL CONFIGURATION =============
  
  // Cargar toda la configuración en una sola llamada
  const loadAllConfiguration = async () => {
    setLoading(prev => ({ ...prev, settings: true, loadingAutomation: true }));
    try {
      const response = await fetch(`${BACKEND}api/temporales-mro/email-config`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Parsear el body que viene como JSON string con \r\n
          let bodyText = '';
          let footerText = '';
          
          try {
            if (data.data.body && typeof data.data.body === 'string') {
              const bodyJson = JSON.parse(data.data.body);
              bodyText = bodyJson.cuerpo || '';
              footerText = bodyJson.footer || '';
            } else if (data.data.body && typeof data.data.body === 'object') {
              bodyText = data.data.body.cuerpo || '';
              footerText = data.data.body.footer || '';
            }
          } catch (e) {
            console.error('Error parsing body JSON:', e);
            bodyText = data.data.body || '';
          }
          
          // Setear email settings
          setEmailSettings({
            subject: data.data.subject || '',
            body: bodyText,
            footer: footerText
          });
          
          // Setear automation config
          setAutomationConfig({
            is_active: data.data.is_active || false,
            hora: data.data.hora || 7,
            dias: data.data.dias || '*'
          });
        }
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar la configuración'
      });
    } finally {
      setLoading(prev => ({ ...prev, settings: false, loadingAutomation: false }));
    }
  };

  const saveEmailConfiguration = async () => {
    setLoading(prev => ({ ...prev, savingEmailSettings: true }));
    try {
      // Construir el JSON con cuerpo y footer para enviarlo al backend
      const bodyJson = JSON.stringify({
        cuerpo: emailSettings.body,
        footer: emailSettings.footer
      });
      
      const response = await fetch(`${BACKEND}api/temporales-mro/email-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: emailSettings.subject,
          body: bodyJson,  // Enviar como JSON string
          user: user
        })
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Configuración de email guardada correctamente',
          timer: 2000
        });
        loadAllConfiguration();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar configuración');
      }
    } catch (error) {
      console.error('Error saving email configuration:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo guardar la configuración'
      });
    } finally {
      setLoading(prev => ({ ...prev, savingEmailSettings: false }));
    }
  };

  const handleEmailSettingChange = (field, value) => {
    setEmailSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ============= FUNCIONES DE GESTIÓN DE EMAILS =============

  const loadSavedEmails = async () => {
    setLoading(prev => ({ ...prev, loadingEmails: true }));
    try {
      const response = await fetch(`${BACKEND}api/temporales-mro/emails/mro-temporales`, {
        method: 'GET',
        skipDatabaseHeader: true,  // No enviar X-Database header por CORS
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const result = await response.json();
        console.log('Response from backend:', result); // Debug
        if (result.success && result.data) {
          // El backend devuelve { success: true, data: [{id, mail}] }
          const emailsFormatted = result.data.map(item => ({
            id: item.id,
            email: item.mail
          }));
          setSavedEmails(emailsFormatted);
        }
      } else {
        console.error('Response not OK:', response.status);
      }
    } catch (error) {
      console.error('Error loading saved emails:', error);
    } finally {
      setLoading(prev => ({ ...prev, loadingEmails: false }));
    }
  };

  const addNewEmail = async () => {
    if (!newEmailInput.trim()) return;

    // Separar emails por comas o punto y coma
    const emails = newEmailInput
      .split(/[,;]/)
      .map(email => email.trim())
      .filter(email => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

    if (emails.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Atención',
        text: 'Por favor ingrese al menos un email válido'
      });
      return;
    }

    setLoading(prev => ({ ...prev, savingEmails: true }));
    try {
      const response = await fetch(`${BACKEND}api/temporales-mro/emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emails: emails,
          user: user
        })
      });

      if (response.ok) {
        const data = await response.json();
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: `${emails.length} email(s) agregado(s) correctamente`,
          timer: 2000
        });
        setNewEmailInput('');
        loadSavedEmails();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al agregar emails');
      }
    } catch (error) {
      console.error('Error adding emails:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudieron agregar los emails'
      });
    } finally {
      setLoading(prev => ({ ...prev, savingEmails: false }));
    }
  };

  const removeEmail = async (emailToRemove) => {
    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: `Se eliminará el email: ${emailToRemove}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setLoading(prev => ({
      ...prev,
      deletingEmails: { ...prev.deletingEmails, [emailToRemove]: true }
    }));

    try {
      // Buscar el ID del email
      const emailObj = savedEmails.find(e => e.email === emailToRemove);
      if (!emailObj) throw new Error('Email no encontrado');

      const response = await fetch(`${BACKEND}api/temporales-mro/emails/${emailObj.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: user })
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Eliminado',
          text: 'Email eliminado correctamente',
          timer: 2000
        });
        loadSavedEmails();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar email');
      }
    } catch (error) {
      console.error('Error removing email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo eliminar el email'
      });
    } finally {
      setLoading(prev => ({
        ...prev,
        deletingEmails: { ...prev.deletingEmails, [emailToRemove]: false }
      }));
    }
  };

  const addEmailToInput = (email) => {
    const currentEmails = newEmailInput.trim();
    if (currentEmails) {
      setNewEmailInput(`${currentEmails}, ${email}`);
    } else {
      setNewEmailInput(email);
    }
  };

  const getFilteredEmails = () => {
    if (!emailFilter) return savedEmails;
    return savedEmails.filter(emailObj => 
      emailObj.email.toLowerCase().includes(emailFilter.toLowerCase())
    );
  };

  const getHeightMultiplier = () => {
    return 35; // Altura aproximada por email badge
  };

  // ============= FUNCIONES DE ENVÍO DE REPORTES =============

  const sendTestEmail = async () => {
    if (savedEmails.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Atención',
        text: 'No hay emails configurados para enviar'
      });
      return;
    }

    const result = await Swal.fire({
      title: '¿Enviar email de prueba?',
      text: `Se enviará a ${savedEmails.length} destinatario(s)`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setLoading(prev => ({ ...prev, sendingEmail: true }));
    try {
      const response = await fetch(`${BACKEND}api/temporales-mro/email-automation/test-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: user })
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Email Enviado',
          text: 'Email de prueba enviado correctamente. Revisa los logs para más detalles.',
          timer: 3000
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al enviar email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo enviar el email'
      });
    } finally {
      setLoading(prev => ({ ...prev, sendingEmail: false }));
    }
  };

  // ============= FUNCIONES DE AUTOMATIZACIÓN =============

  const saveAutomationConfig = async () => {
    setLoading(prev => ({ ...prev, savingAutomation: true }));
    try {
      const response = await fetch(`${BACKEND}api/temporales-mro/email-automation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_active: automationConfig.is_active,
          hora: automationConfig.hora,
          dias: automationConfig.dias,
          user: user
        })
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Configuración de automatización guardada correctamente',
          timer: 2000
        });
        
        // Recargar automatización
        await reloadAutomation();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar configuración');
      }
    } catch (error) {
      console.error('Error saving automation config:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo guardar la configuración'
      });
    } finally {
      setLoading(prev => ({ ...prev, savingAutomation: false }));
    }
  };

  const reloadAutomation = async () => {
    try {
      const response = await fetch(`${BACKEND}api/temporales-mro/email-automation/reload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: user })
      });

      if (response.ok) {
        console.log('Automatización recargada correctamente');
      }
    } catch (error) {
      console.error('Error reloading automation:', error);
    }
  };

  const handleAutomationConfigChange = (field, value) => {
    setAutomationConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ============= FUNCIONES DE CONSOLA =============

  const openConsole = () => {
    setConsoleModal(true);
    fetchConsoleLog();
    fetchEmailAutomationStatus();
  };

  const handleConsoleTabChange = (tab) => {
    setConsoleActiveTab(tab);
    if (tab === 'log') {
      fetchConsoleLog();
    } else if (tab === 'status') {
      fetchEmailAutomationStatus();
    }
  };

  const fetchConsoleLog = async () => {
    setLoading(prev => ({ ...prev, log: true }));
    try {
      const response = await fetch(`${BACKEND}api/temporales-mro/log`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConsoleLog(data.log || 'No hay logs disponibles');
        }
      }
    } catch (error) {
      console.error('Error fetching console log:', error);
      setConsoleLog('Error al cargar los logs');
    } finally {
      setLoading(prev => ({ ...prev, log: false }));
    }
  };

  const fetchEmailAutomationStatus = async () => {
    setLoading(prev => ({ ...prev, status: true }));
    try {
      const response = await fetch(`${BACKEND}api/temporales-mro/email-automation/status`);
      if (response.ok) {
        const data = await response.json();
        setEmailAutomationStatus(data);
      }
    } catch (error) {
      console.error('Error fetching email automation status:', error);
      setEmailAutomationStatus({ error: 'Error al cargar el estado' });
    } finally {
      setLoading(prev => ({ ...prev, status: false }));
    }
  };

  const handleToggle = () => {
    toggle();
  };



  return (
    <Modal isOpen={isOpen} toggle={handleToggle} size="xl" style={{ maxWidth: '95%' }}>
      <ModalHeader toggle={handleToggle} className="bg-primary text-white">
        <div className="d-flex align-items-center">
          <div className="icon-circle bg-white me-3" style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="bell" style={{ fontSize: 20, color: '#7c3aed' }} />
          </div>
          <div style={{ marginLeft: '5px' }}>
            <h4 className="mb-0">Configuración de Avisos - MRO Temporales</h4>
            <small className="opacity-8">Ajustes de Avisos Via Mail</small>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="p-0">
        {loading.settings ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
            <div className="text-center">
              <Spinner color="primary" size="lg" />
              <p className="mt-3 text-muted">Cargando configuración...</p>
            </div>
          </div>
        ) : (
          <div className="row g-0">
            {/* Sidebar de navegación */}
            <div className="col-md-3 bg-light border-end">
              <div className="p-4">
                <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                  Configuraciones
                </h6>
                <div className="nav flex-column nav-pills">
                  <button
                    className={`nav-link d-flex align-items-center mb-2 ${activeTab === 'email' ? 'active' : ''}`}
                    style={{
                      textAlign: 'left',
                      border: 'none',
                      background: activeTab === 'email' ? 'rgba(94, 114, 228, 0.1)' : 'transparent',
                      color: activeTab === 'email' ? '#5e72e4' : '#6c757d'
                    }}
                    onClick={() => setActiveTab('email')}
                  >
                    <Icon name="mail" style={{ marginRight: '5px' }} />
                    Envio de Avisos
                  </button>
                  <button
                    className={`nav-link d-flex align-items-center mb-2 ${activeTab === 'configuracion' ? 'active' : ''}`}
                    style={{
                      textAlign: 'left',
                      border: 'none',
                      background: activeTab === 'configuracion' ? 'rgba(94, 114, 228, 0.1)' : 'transparent',
                      color: activeTab === 'configuracion' ? '#5e72e4' : '#6c757d'
                    }}
                    onClick={() => setActiveTab('configuracion')}
                  >
                    <Icon name="setting" style={{ marginRight: '5px' }} />
                    Configuracion
                  </button>
                </div>
              </div>
            </div>

            {/* Contenido principal */}
            <div className="col-md-9">
              <div className="p-4">
                {/* Contenido de Notificaciones Email */}
                {activeTab === 'email' && (
                  <div>
                    <div className="row g-3 mb-4">
                      <div className="col-12">
                        <div className="card" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                          <div className="card-body">
                            <h6 className="card-title mb-3">Configuración del Email</h6>
                            <div className="row">
                              <div className="col-md-12 mb-3">
                                <label className="form-label">Asunto del Email</label>
                                <Input
                                  type="text"
                                  value={emailSettings.subject}
                                  onChange={(e) => handleEmailSettingChange('subject', e.target.value)}
                                  placeholder="Ingrese el asunto del email"
                                />
                              </div>
                              <div className="col-md-12 mb-3">
                                <label className="form-label">Cuerpo del Email</label>
                                <Input
                                  type="textarea"
                                  value={emailSettings.body}
                                  onChange={(e) => handleEmailSettingChange('body', e.target.value)}
                                  placeholder="Ingrese el cuerpo del email"
                                  rows="3"
                                />
                              </div>
                              <div className="col-md-12">
                                <label className="form-label">Footer del Email</label>
                                <Input
                                  type="textarea"
                                  value={emailSettings.footer}
                                  onChange={(e) => handleEmailSettingChange('footer', e.target.value)}
                                  placeholder="Ingrese el footer del email"
                                  rows="2"
                                />
                              </div>
                            </div>
                            <div className="row mt-3">
                              <div className="col-12">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={saveEmailConfiguration}
                                  className="d-flex align-items-center"
                                  disabled={loading.savingEmailSettings}
                                >
                                  {loading.savingEmailSettings ? (
                                    <>
                                      <Spinner size="sm" color="light" />
                                      <span style={{ marginLeft: 5 }}>Guardando...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Icon name="save" />
                                      <span style={{ marginLeft: 5 }}>Guardar Configuración</span>
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label">
                          Nuevos Emails (separados por comas o punto y coma)
                          <small className="text-muted ms-2"> - Para agregar a la lista guardada</small>
                        </label>
                        <Input
                          type="textarea"
                          value={newEmailInput}
                          onChange={(e) => setNewEmailInput(e.target.value)}
                          placeholder="email1@ejemplo.com, email2@ejemplo.com; email3@ejemplo.com"
                          rows="3"
                        />
                      </div>

                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label">
                            Emails Guardados - {savedEmails.length}
                          </label>
                          <Button
                            color="success"
                            size="sm"
                            onClick={addNewEmail}
                            className="d-flex align-items-center"
                            disabled={!newEmailInput.trim() || loading.savingEmails}
                          >
                            {loading.savingEmails ? (
                              <>
                                <Spinner size="sm" color="light" />
                                <span style={{ marginLeft: 5 }}>Guardando...</span>
                              </>
                            ) : (
                              <>
                                <Icon name="plus" />
                                <span style={{ marginLeft: 5 }}>Agregar a Lista</span>
                              </>
                            )}
                          </Button>
                        </div>
                        {loading.loadingEmails ? (
                          <div className="text-center py-3">
                            <Spinner size="sm" color="primary" />
                            <span className="ms-2 text-muted">Cargando emails...</span>
                          </div>
                        ) : savedEmails.length > 0 ? (
                          <>
                            {/* Buscador de emails */}
                            <div className="mb-3">
                              <Input
                                type="text"
                                placeholder="🔍 Buscar emails..."
                                value={emailFilter}
                                onChange={(e) => setEmailFilter(e.target.value)}
                                className="form-control-sm"
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '20px',
                                  padding: '8px 16px',
                                  fontSize: '14px'
                                }}
                              />
                              {emailFilter && (
                                <small className="text-muted mt-1 d-block">
                                  Mostrando {getFilteredEmails().length} de {savedEmails.length} emails
                                </small>
                              )}
                            </div>

                            <div
                              className="emails-container"
                              style={{
                                height: `${Math.min((getFilteredEmails().length * getHeightMultiplier() + 15) > 120 ? (getFilteredEmails().length * getHeightMultiplier() + 15) : 120, 350)}px`,
                                overflowY: ((getFilteredEmails().length * getHeightMultiplier() + 15) > 120 ? (getFilteredEmails().length * getHeightMultiplier() + 15) : 120) > 350 ? 'auto' : 'visible',
                                border: '1px solid #dee2e6',
                                borderRadius: '8px',
                                padding: '15px',
                                backgroundColor: '#f8f9fa'
                              }}
                            >
                              <div className="d-flex flex-wrap gap-2">
                                {getFilteredEmails().length > 0 ? (
                                  getFilteredEmails().map((emailObj, index) => (
                                    <div key={index} className="email-badge badge bg-light text-dark border d-flex align-items-center" style={{ marginRight: '5px', marginBottom: '5px' }}>
                                      <span
                                        className="cursor-pointer me-2"
                                        onClick={() => addEmailToInput(emailObj.email)}
                                        title="Click para agregar al campo de destinatarios"
                                        style={{ marginRight: '5px', cursor: 'pointer' }}
                                      >
                                        {emailObj.email}
                                      </span>
                                      {loading.deletingEmails[emailObj.email] ? (
                                        <Spinner size="sm" color="primary" />
                                      ) : (
                                        <button
                                          type="button"
                                          className="btn-close btn-close-sm"
                                          onClick={() => removeEmail(emailObj.email)}
                                          style={{ marginLeft: '5px' }}
                                          title="Eliminar email"
                                        ></button>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-center w-100 py-3 text-muted">
                                    <Icon name="search" style={{ fontSize: '24px', marginBottom: '8px' }} />
                                    <div>No se encontraron emails que coincidan con "{emailFilter}"</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-muted">
                            <small>No hay emails guardados. Ingrese emails en el campo de arriba y haga clic en "Agregar a Lista".</small>
                          </div>
                        )}
                      </div>

                      <div className="col-12">
                        <Button
                          color="warning"
                          size="lg"
                          block
                          onClick={sendTestEmail}
                          disabled={loading.sendingEmail || savedEmails.length === 0}
                        >
                          {loading.sendingEmail ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Enviando...
                            </>
                          ) : (
                            <>
                              <Icon name="send" />
                              <span style={{ marginLeft: 5 }}>Enviar Email de Prueba ({savedEmails.length})</span>
                            </>
                          )}
                        </Button>
                      </div>

                      <div className="col-12">
                        <Alert color="info">
                          <p className="mb-2"><strong>Avisos de Vencimiento MRO Temporales:</strong></p>
                          <p className="mb-2">Envía alertas de vencimiento de temporales a los emails configurados.</p>
                          <ul className="mb-2">
                            <li><strong>Contenido:</strong> Email individual por cada temporal próxima a vencer (60, 30, 10 días)</li>
                            <li><strong>Adjunto:</strong> Excel con todas las temporales activas</li>
                            <li><strong>Destinatarios:</strong> Emails configurados en esta sección</li>
                            <li><strong>Automatización:</strong> Configurable en la pestaña "Configuración"</li>
                          </ul>
                        </Alert>
                      </div>
                    </div>
                  </div>
                )}

                {/* Configuracion */}
                {activeTab === 'configuracion' && (
                  <div>
                    <div className="row g-4">
                      <div className="col-12">
                        <h5 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>Configuración de Envíos</span>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={openConsole}
                          >
                            <Icon name="monitor" style={{ marginRight: '5px' }} />
                            Consola
                          </Button>
                        </h5>
                        <p className="text-muted">
                          Configure los horarios y estado de los avisos automáticos.
                        </p>
                      </div>

                      {/* Configuración Automatización */}
                      <div className="col-lg-12">
                        <div className="card" style={{ border: '1px solid #dee2e6' }}>
                          <div className="card-header bg-success text-white">
                            <h6 className="mb-0">Automatización de Avisos</h6>
                            <small>Envío automático de alertas de vencimiento</small>
                          </div>
                          <div className="card-body">
                            {loading.loadingAutomation ? (
                              <div className="text-center py-3">
                                <Spinner color="success" />
                                <p className="mt-2 text-muted">Cargando configuración...</p>
                              </div>
                            ) : (
                              <div className="row g-3">
                                <div className="col-12">
                                  <div className="form-check form-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="automationActive"
                                      checked={automationConfig.is_active || false}
                                      onChange={(e) => handleAutomationConfigChange('is_active', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="automationActive">
                                      Automatización activa
                                    </label>
                                  </div>
                                </div>

                                <div className="col-12">
                                  <label className="form-label">Hora de ejecución (Hora Argentina)</label>
                                  <Input
                                    type="number"
                                    min="0"
                                    max="23"
                                    value={automationConfig.hora || 7}
                                    onChange={(e) => handleAutomationConfigChange('hora', parseInt(e.target.value))}
                                  />
                                  <small className="text-muted">Hora en formato 24h (0-23). Por defecto: 7:00 AM</small>
                                </div>

                                <div className="col-12">
                                  <label className="form-label">Días de ejecución</label>
                                  <Input
                                    type="text"
                                    value="Diario (*) - Envío 60, 30 y 10 días antes del vencimiento"
                                    disabled
                                  />
                                  <small className="text-muted">
                                    El sistema verifica diariamente y envía alertas solo para temporales que vencen en exactamente 60, 30 o 10 días
                                  </small>
                                </div>

                                <div className="col-12">
                                  <Button
                                    color="success"
                                    onClick={saveAutomationConfig}
                                    disabled={loading.savingAutomation}
                                    className="d-flex align-items-center"
                                  >
                                    {loading.savingAutomation ? (
                                      <>
                                        <Spinner size="sm" color="light" />
                                        <span style={{ marginLeft: 5 }}>Guardando...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Icon name="save" />
                                        <span style={{ marginLeft: 5 }}>Guardar Configuración</span>
                                      </>
                                    )}
                                  </Button>
                                </div>

                                <div className="col-12">
                                  <Alert color="info">
                                    <small>
                                      <strong>Estado actual:</strong> {automationConfig.is_active ? '✅ Activo' : '❌ Inactivo'}<br />
                                      <strong>Próxima ejecución:</strong> Diario a las {automationConfig.hora || 7}:00 hs (Argentina)<br />
                                      <strong>Destinatarios:</strong> {savedEmails.length} email(s) configurado(s)
                                    </small>
                                  </Alert>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <Alert color="warning">
                          <h6>⚠️ Información importante sobre la automatización:</h6>
                          <ul className="mb-0">
                            <li><strong>Zona horaria:</strong> Las horas se ejecutan en hora de Argentina (UTC-3)</li>
                            <li><strong>Destinatarios:</strong> Los emails se envían a las listas configuradas en la pestaña "Envío de Avisos"</li>
                            <li><strong>Contenido:</strong> Los asuntos y cuerpos de email se configuran en la pestaña "Envío de Avisos"</li>
                            <li><strong>Frecuencia:</strong> El sistema verifica diariamente y envía alertas para temporales que vencen en 60, 30 o 10 días exactos</li>
                            <li><strong>Formato:</strong> Cada temporal genera un email individual con Excel adjunto conteniendo todas las temporales activas</li>
                            <li><strong>Recargar:</strong> Después de guardar cambios, la automatización se recarga automáticamente</li>
                          </ul>
                        </Alert>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="bg-light border-top">
        <div className="w-100 d-flex justify-content-between align-items-center">
          <small className="text-muted">
            <Icon name="info" style={{ marginRight: '5px' }} />
            Las configuraciones se guardan automáticamente
          </small>
          <Button color="secondary" onClick={handleToggle}>
            <Icon name="cross" style={{ marginRight: '5px' }} />
            Cerrar
          </Button>
        </div>
      </ModalFooter>

      {/* Modal de Consola */}
      <Modal
        isOpen={consoleModal}
        toggle={() => setConsoleModal(!consoleModal)}
        size="xl"
        style={{ maxWidth: '95%' }}
      >
        <ModalHeader toggle={() => setConsoleModal(!consoleModal)}>
          <Icon name="monitor" style={{ marginRight: '5px' }} />
          <span>Consola del Sistema - MRO Temporales</span>
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-12 mb-3">
              <Nav tabs className="mb-3">
                <NavItem>
                  <NavLink
                    className={classnames({ active: consoleActiveTab === 'log' })}
                    onClick={() => handleConsoleTabChange('log')}
                    style={{ cursor: 'pointer' }}
                  >
                    <Icon name="file-text" style={{ marginRight: '5px' }} />
                    Log del Sistema
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: consoleActiveTab === 'status' })}
                    onClick={() => handleConsoleTabChange('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    <Icon name="activity" style={{ marginRight: '5px' }} />
                    Estado de Automatización
                  </NavLink>
                </NavItem>
              </Nav>
            </div>

            <div className="col-12">
              <TabContent activeTab={consoleActiveTab}>
                {/* Tab de Log */}
                <TabPane tabId="log">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        <Icon name="file-text" style={{ marginRight: '5px' }} />
                        Archivo: temporales_mro.log
                      </h6>
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={fetchConsoleLog}
                        disabled={loading.log}
                      >
                        {loading.log ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <Icon name="reload" style={{ marginRight: '5px' }} />
                            Actualizar
                          </>
                        )}
                      </Button>
                    </div>
                    <div className="card-body p-0">
                      {loading.log ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                          <div className="text-center">
                            <Spinner color="primary" />
                            <p className="mt-2 text-muted">Cargando log...</p>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            height: '500px',
                            overflowY: 'auto',
                            backgroundColor: '#1e1e1e',
                            color: '#d4d4d4',
                            fontFamily: 'Consolas, Monaco, monospace',
                            fontSize: '12px',
                            padding: '15px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                          dangerouslySetInnerHTML={{
                            __html: consoleLog.replace(/\n/g, '<br>')
                          }}
                        />
                      )}
                    </div>
                  </div>
                </TabPane>

                {/* Tab de Estado */}
                <TabPane tabId="status">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        <Icon name="activity" style={{ marginRight: '5px' }} />
                        Estado de la Automatización de Emails MRO Temporales
                      </h6>
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={fetchEmailAutomationStatus}
                        disabled={loading.status}
                      >
                        {loading.status ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <Icon name="reload" style={{ marginRight: '5px' }} />
                            Actualizar
                          </>
                        )}
                      </Button>
                    </div>
                    <div className="card-body">
                      {loading.status ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                          <div className="text-center">
                            <Spinner color="primary" />
                            <p className="mt-2 text-muted">Cargando estado...</p>
                          </div>
                        </div>
                      ) : emailAutomationStatus ? (
                        emailAutomationStatus.error ? (
                          <Alert color="danger">
                            <Icon name="alert-circle" style={{ marginRight: '5px' }} />
                            {emailAutomationStatus.error}
                          </Alert>
                        ) : (
                          <div className="row g-3">
                            {/* Información general del sistema */}
                            <div className="col-12">
                              <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '15px', marginBottom: '15px' }}>
                                <h6 className="mb-3">
                                  <Icon name="globe" style={{ marginRight: '5px' }} />
                                  Estado General del Sistema
                                </h6>
                                <div className="row">
                                  <div className="col-md-6">
                                    <p className="mb-2">
                                      <span style={{
                                        display: 'inline-block',
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: emailAutomationStatus.scheduler_running ? '#2dce89' : '#f5365c',
                                        marginRight: '8px'
                                      }}></span>
                                      <strong>Scheduler:</strong> {emailAutomationStatus.scheduler_running ? 'Activo' : 'Inactivo'}
                                    </p>
                                  </div>
                                  <div className="col-md-6">
                                    <p className="mb-2">
                                      <span style={{
                                        display: 'inline-block',
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: emailAutomationStatus.automation_available ? '#2dce89' : '#f5365c',
                                        marginRight: '8px'
                                      }}></span>
                                      <strong>Automatización:</strong> {emailAutomationStatus.automation_available ? 'Disponible' : 'No Disponible'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Procesos activos */}
                            {emailAutomationStatus.active_processes && emailAutomationStatus.active_processes.length > 0 && (
                              <div className="col-12">
                                <h6 className="mb-3">
                                  <Icon name="settings" style={{ marginRight: '5px' }} />
                                  Procesos de Automatización Configurados
                                </h6>
                                <div className="row">
                                  {emailAutomationStatus.active_processes.map((process, index) => (
                                    <div key={index} className="col-md-12 mb-3">
                                      <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '15px' }}>
                                        <h6 className="mb-2">
                                          🔔 {process.type === 'AVISO_VENCIMIENTO_MRO_TEMPORALES' ? 'Avisos de Vencimiento MRO Temporales' : process.type}
                                        </h6>
                                        <p className="mb-2">
                                          <span style={{
                                            display: 'inline-block',
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '50%',
                                            backgroundColor: '#2dce89',
                                            marginRight: '8px'
                                          }}></span>
                                          <strong>Estado:</strong> Configurado y Activo
                                        </p>
                                        <p className="mb-2">
                                          <strong>Horario Argentina:</strong> {process.hora_argentina}:00 hs
                                        </p>
                                        <p className="mb-2">
                                          <strong>Horario UTC:</strong> {process.hora_utc}:00 hs
                                        </p>
                                        <p className="mb-2">
                                          <strong>Días:</strong> {process.dias === '*' ? 'Diario' : process.dias}
                                        </p>
                                        <p className="mb-2">
                                          <strong>Asunto:</strong> {process.asunto_mail}
                                        </p>
                                        <div className="mt-2">
                                          <small className="text-muted">
                                            <strong>Cuerpo del Mensaje:</strong><br />
                                            <div style={{
                                              whiteSpace: 'pre-wrap',
                                              wordWrap: 'break-word',
                                              maxHeight: '120px',
                                              overflowY: 'auto',
                                              padding: '8px',
                                              backgroundColor: '#ffffff',
                                              borderRadius: '4px',
                                              border: '1px solid #e9ecef',
                                              fontSize: '11px',
                                              lineHeight: '1.4',
                                              marginTop: '5px'
                                            }}>
                                              {(() => {
                                                try {
                                                  if (typeof process.cuerpo_mail === 'string') {
                                                    const parsed = JSON.parse(process.cuerpo_mail);
                                                    return parsed.cuerpo || process.cuerpo_mail;
                                                  } else if (typeof process.cuerpo_mail === 'object' && process.cuerpo_mail.cuerpo) {
                                                    return process.cuerpo_mail.cuerpo;
                                                  }
                                                  return JSON.stringify(process.cuerpo_mail, null, 2);
                                                } catch (e) {
                                                  return process.cuerpo_mail;
                                                }
                                              })()}
                                            </div>
                                          </small>
                                          <small className="text-muted mt-2 d-block">
                                            <strong>Footer del Mensaje:</strong><br />
                                            <div style={{
                                              whiteSpace: 'pre-wrap',
                                              wordWrap: 'break-word',
                                              maxHeight: '80px',
                                              overflowY: 'auto',
                                              padding: '8px',
                                              backgroundColor: '#ffffff',
                                              borderRadius: '4px',
                                              border: '1px solid #e9ecef',
                                              fontSize: '11px',
                                              lineHeight: '1.4',
                                              marginTop: '5px'
                                            }}>
                                              {(() => {
                                                try {
                                                  if (typeof process.cuerpo_mail === 'string') {
                                                    const parsed = JSON.parse(process.cuerpo_mail);
                                                    return parsed.footer || 'Sin footer configurado';
                                                  } else if (typeof process.cuerpo_mail === 'object' && process.cuerpo_mail.footer) {
                                                    return process.cuerpo_mail.footer;
                                                  }
                                                  return 'Sin footer configurado';
                                                } catch (e) {
                                                  return 'Sin footer configurado';
                                                }
                                              })()}
                                            </div>
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Trabajos programados */}
                            {emailAutomationStatus.scheduled_jobs && emailAutomationStatus.scheduled_jobs.length > 0 && (
                              <div className="col-12">
                                <h6 className="mb-3">
                                  <Icon name="clock" style={{ marginRight: '5px' }} />
                                  Trabajos Programados en el Scheduler
                                </h6>
                                <div className="table-responsive">
                                  <Table className="table" responsive>
                                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                                      <tr>
                                        <th>ID del Trabajo</th>
                                        <th>Nombre</th>
                                        <th>Próxima Ejecución</th>
                                        <th>Trigger</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {emailAutomationStatus.scheduled_jobs.map((job, index) => (
                                        <tr key={index}>
                                          <td>
                                            <span className="badge bg-primary">{job.id}</span>
                                          </td>
                                          <td>{job.name}</td>
                                          <td>{job.next_run || 'No programada'}</td>
                                          <td style={{
                                            wordWrap: 'break-word',
                                            whiteSpace: 'normal',
                                            fontSize: '12px',
                                            lineHeight: '1.4'
                                          }}>
                                            {job.trigger}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </div>
                            )}

                            {/* Estado cuando no hay procesos activos */}
                            {(!emailAutomationStatus.active_processes || emailAutomationStatus.active_processes.length === 0) &&
                              (!emailAutomationStatus.scheduled_jobs || emailAutomationStatus.scheduled_jobs.length === 0) && (
                                <div className="col-12">
                                  <Alert color="info">
                                    <Icon name="info" style={{ marginRight: '5px' }} />
                                    <strong>Sin procesos configurados:</strong> No hay procesos de automatización activos en este momento.
                                    Configure los procesos desde la pestaña "Configuración".
                                  </Alert>
                                </div>
                              )}
                          </div>
                        )
                      ) : (
                        <div className="text-center text-muted" style={{ padding: '50px' }}>
                          <Icon name="arrow-right" style={{ fontSize: '48px', marginBottom: '15px' }} />
                          <p>Haz clic en "Actualizar" para cargar el estado del sistema</p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabPane>
              </TabContent>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </Modal>
  );
};

export default TemporalesMroAvisos;